import React, { useState, useEffect } from "react";
import axios from "axios";
import { EmailGuard } from "../App";
import { AuthGuard } from "../App";
import { 
    FaServer, 
    FaBell, 
    FaSpinner, 
    FaClock, 
    FaCalendarAlt, 
    FaExclamationTriangle, 
    FaChevronLeft, 
    FaChevronRight 
} from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";


const RateLimit = () => {
    // State for data and loading
    const [ipData, setIPData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // You can adjust this value

    // Helper function to format timestamp
    const formatDateTime = (timestamp) => {
        if (!timestamp) return "N/A";
        try {
            // Assuming timestamp is in ISO format string
            return new Date(timestamp).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (e) {
            return timestamp; // Return raw if formatting fails
        }
    };

    useEffect(() => {
        const fetchIPData = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(
                    `${StandardURL}/iNyumba/view/ratelimits`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                
                // Ensure data is an array before setting
                const responseData = Array.isArray(response.data.response) ? response.data.response : [];
                setIPData(responseData);
                setLoading(false);
            } catch (error) {
                // Enhanced error handling
                const errorMessage = error.response?.data?.response || error.message || "Failed to fetch rate limit data.";
                setError(errorMessage);
                setLoading(false);
            }
        };
        fetchIPData();
    }, []);

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = ipData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(ipData.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

    // Determine which page numbers to show (e.g., max 5 buttons)
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;
        let startPage, endPage;

        if (totalPages <= maxPagesToShow) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const maxPagesBeforeCurrentPage = Math.floor(maxPagesToShow / 2);
            const maxPagesAfterCurrentPage = Math.ceil(maxPagesToShow / 2) - 1;

            if (currentPage <= maxPagesBeforeCurrentPage) {
                startPage = 1;
                endPage = maxPagesToShow;
            } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
                startPage = totalPages - maxPagesToShow + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - maxPagesBeforeCurrentPage;
                endPage = currentPage + maxPagesAfterCurrentPage;
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }
        return pageNumbers;
    };
    // End Pagination Logic

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8">
                <Oval color="#4f46e5" secondaryColor="#a5b4fc" height={50} width={50} ariaLabel="loading-spinner" />
                <p className="mt-4 text-lg text-gray-600">Loading IP Rate Limit Data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-16 px-4 bg-white shadow-xl rounded-xl max-w-lg mx-auto mt-10 border-t-4 border-red-500">
                <FaExclamationTriangle className="w-10 h-10 mx-auto text-red-500 mb-4" />
                <h2 className="text-xl font-semibold text-red-700 mb-2">Error Loading Data</h2>
                <p className="text-gray-600">Details: {error}</p>
            </div>
        );
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-full mx-auto">
                        <h1 className="text-3xl font-extrabold text-gray-900 flex items-center mb-6 border-b pb-4">
                            <FaServer className="mr-3 text-indigo-600" /> API Rate Limit Monitoring
                        </h1>

                        {ipData.length === 0 ? (
                            <div className="p-12 text-center bg-white shadow-xl rounded-xl">
                                <FaBell className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                                <p className="text-lg font-medium text-gray-600">No Rate Limit records found.</p>
                            </div>
                        ) : (
                            <>
                                <div className="shadow-xl rounded-xl overflow-hidden border border-gray-200">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-indigo-600">
                                                <tr>
                                                    <th className="py-3 px-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                                        ID
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                                        IP Address
                                                    </th>
                                                    <th className="py-3 px-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                                                        Requests
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                                        <FaClock className="inline mr-1" /> Last Request
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                                        <FaCalendarAlt className="inline mr-1" /> Created At
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                                        Updated At
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {currentItems.map((item) => (
                                                    <tr 
                                                        key={item.id} 
                                                        // Highlight row for high request counts (> 50)
                                                        className={`hover:bg-indigo-50 transition duration-150 ${item.request_count > 50 ? 'bg-red-50' : ''}`}
                                                    >
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {item.id}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-800 font-mono">
                                                            {item.ip_address}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm font-bold text-center">
                                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${item.request_count > 50 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                                {item.request_count}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDateTime(item.last_request_at)}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDateTime(item.created_at)}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDateTime(item.updated_at)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex justify-between items-center py-4 px-4 bg-white shadow-xl rounded-xl mt-4">
                                        <p className="text-sm text-gray-700">
                                            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, ipData.length)}</span> of <span className="font-medium">{ipData.length}</span> results
                                        </p>
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                            <button
                                                onClick={prevPage}
                                                disabled={currentPage === 1}
                                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                <FaChevronLeft className="h-4 w-4" />
                                            </button>
                                            
                                            {getPageNumbers().map(number => (
                                                <button
                                                    key={number}
                                                    onClick={() => paginate(number)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                        currentPage === number
                                                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {number}
                                                </button>
                                            ))}

                                            <button
                                                onClick={nextPage}
                                                disabled={currentPage === totalPages}
                                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                <FaChevronRight className="h-4 w-4" />
                                            </button>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default RateLimit;